/**
 * Locale-aware formatting for Teivor (Czech-first). Uses the native `Intl` API —
 * no luxon/date-fns runtime. Czech conventions: thousands grouped with a
 * (narrow) non-breaking space `1 000`, decimal comma `3,14`, currency symbol
 * after the amount `100 Kč`, dates `1. 2. 2026`. English (`en-US`) is the
 * secondary locale. Every numeric surface that renders these also sets
 * `font-variant-numeric: tabular-nums` (handoff §3.3) — that is a CSS concern.
 */

export type Locale = 'cs-CZ' | 'en-US';
export const DEFAULT_LOCALE: Locale = 'cs-CZ';

/** Narrow no-break space (U+202F) — what `Intl` cs-CZ uses to group thousands. */
export const NNBSP = ' ';
/** No-break space (U+00A0) — for hand-built strings ("100 Kč", "1. 2. 2026"). */
export const NBSP = ' ';

const numberCache = new Map<string, Intl.NumberFormat>();
function nf(locale: Locale, opts: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = locale + JSON.stringify(opts);
  let f = numberCache.get(key);
  if (!f) {
    f = new Intl.NumberFormat(locale, opts);
    numberCache.set(key, f);
  }
  return f;
}

/** Integer / grouped number: `1 234 567` (cs) · `1,234,567` (en). */
export function formatNumber(
  value: number,
  locale: Locale = DEFAULT_LOCALE,
  opts: Intl.NumberFormatOptions = {},
): string {
  return nf(locale, opts).format(value);
}

/** Decimal with comma in cs: `3,14`. `digits` fixes fraction length. */
export function formatDecimal(
  value: number,
  locale: Locale = DEFAULT_LOCALE,
  digits = 2,
): string {
  return nf(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

/** Currency: `100 Kč` (CZK, cs) · `€1,000.00` (EUR, en). Default CZK. */
export function formatCurrency(
  value: number,
  currency = 'CZK',
  locale: Locale = DEFAULT_LOCALE,
  opts: Intl.NumberFormatOptions = {},
): string {
  return nf(locale, { style: 'currency', currency, ...opts }).format(value);
}

/** Percent: `12,5 %` (cs). Pass the raw ratio (0.125) or set `fromRatio:false`. */
export function formatPercent(
  value: number,
  locale: Locale = DEFAULT_LOCALE,
  { digits = 1, fromRatio = true }: { digits?: number; fromRatio?: boolean } = {},
): string {
  return nf(locale, {
    style: 'percent',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(fromRatio ? value : value / 100);
}

function toDate(value: Date | string | number): Date {
  return value instanceof Date ? value : new Date(value);
}

const dateTimeCache = new Map<string, Intl.DateTimeFormat>();
function dtf(locale: Locale, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = locale + JSON.stringify(opts);
  let f = dateTimeCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, opts);
    dateTimeCache.set(key, f);
  }
  return f;
}

/** Short date: `1. 2. 2026` (cs) · `2/1/2026` (en). */
export function formatDate(
  value: Date | string | number,
  locale: Locale = DEFAULT_LOCALE,
): string {
  return dtf(locale, { day: 'numeric', month: 'numeric', year: 'numeric' }).format(toDate(value));
}

/** Long date: `1. února 2026` (cs). */
export function formatDateLong(
  value: Date | string | number,
  locale: Locale = DEFAULT_LOCALE,
): string {
  return dtf(locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(toDate(value));
}

/** Date + time: `1. 2. 2026 14:30` (cs). */
export function formatDateTime(
  value: Date | string | number,
  locale: Locale = DEFAULT_LOCALE,
): string {
  return dtf(locale, {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(toDate(value));
}

/** Time only: `14:30` (cs, 24h). */
export function formatTime(
  value: Date | string | number,
  locale: Locale = DEFAULT_LOCALE,
): string {
  return dtf(locale, { hour: '2-digit', minute: '2-digit' }).format(toDate(value));
}

/** ISO date `2026-06-16` — for sortable/technical contexts (handoff §10.5). */
export function formatDateISO(value: Date | string | number): string {
  const d = toDate(value);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Parse a locale-formatted number string back to a number (for inputs).
 * Handles cs grouping spaces (incl. NBSP/NNBSP) + decimal comma. Returns NaN on
 * unparseable input.
 */
export function parseLocaleNumber(input: string, locale: Locale = DEFAULT_LOCALE): number {
  if (typeof input !== 'string') return NaN;
  // The two characters after \s below are U+00A0 NO-BREAK SPACE and U+202F NARROW
  // NO-BREAK SPACE — the thousands separators cs-CZ number formatting actually emits.
  // They are the point of this character class, not a typo: applying the rule's "fix"
  // (deleting them) would silently stop "1 234,56" from parsing. Disabled deliberately.
  // eslint-disable-next-line no-irregular-whitespace
  let s = input.trim().replace(/[\s  ]/g, '');
  if (locale === 'cs-CZ') {
    s = s.replace(/\./g, '').replace(',', '.');
  } else {
    s = s.replace(/,/g, '');
  }
  if (s === '' || s === '-' || s === '.') return NaN;
  return Number(s);
}

/** Empty-cell marker for tables (handoff §10.5): renders "–" (en dash). */
export const EMPTY_CELL = '–';
